import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ProfessionalMarksheet.css';

const ProfessionalMarksheet = ({ user, onClose }) => {
  const [studentInfo, setStudentInfo] = useState({
    name: '',
    fatherName: '',
    class: '',
    studentId: '',
    promotionClass: '',
    attendance: '',
    email: '',
    phone: ''
  });

  const [isLoadingStudent, setIsLoadingStudent] = useState(false);
  const [studentError, setStudentError] = useState('');

  const [schoolInfo, setSchoolInfo] = useState({
    name: 'The Rising School',
    address: 'Savina, Udaipur, Rajasthan',
    academicYear: '2024-25'
  });

  const [remarks, setRemarks] = useState({
    drawing: '',
    discipline: '',
    conduct: ''
  });

  const [marks, setMarks] = useState({});
  const [totals, setTotals] = useState({
    examTotals: { term1: 0, term2: 0, halfYearly: 0, term3: 0, project: 0, annual: 0 },
    grandTotal: 0,
    percentage: 0,
    division: '-',
    result: '-',
    overallGrade: '-',
    maxMarks: 0,
    maxMarksPerSubject: 0
  });

  // Fetch student data by ID or Email
  const fetchStudentData = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setStudentError('');
      return;
    }

    try {
      setIsLoadingStudent(true);
      setStudentError('');
      
      // Try multiple search approaches
      let response;
      let student = null;
      
      // First, try to search by email (if searchTerm contains @)
      if (searchTerm.includes('@')) {
        try {
          response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users?email=${searchTerm}&role=student`);
          if (response.data && response.data.length > 0) {
            student = response.data[0];
          }
        } catch (error) {
          console.log('Email search failed, trying other methods');
        }
      }
      
      // If not found by email, try by student ID
      if (!student) {
        try {
          response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/student/${searchTerm}`);
          student = response.data;
        } catch (error) {
          console.log('Student ID search failed, trying general search');
        }
      }
      
      // If still not found, try general search (name, studentId, etc.)
      if (!student) {
        try {
          response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users?search=${searchTerm}&role=student`);
          if (response.data && response.data.length > 0) {
            student = response.data[0];
          }
        } catch (error) {
          console.log('General search failed');
        }
      }

      if (student) {
        setStudentInfo(prev => ({
          ...prev,
          name: student.name || '',
          fatherName: student.fathersName || student.fatherName || student.parentName || '',
          class: student.class || '',
          studentId: student.email || student.studentId || student._id || '',
          email: student.email || '',
          phone: student.phone || student.phoneNumber || student.mobile || ''
        }));
        setStudentError('');
        console.log('Student found:', student.name);
      } else {
        setStudentError('Student not found. Try using email address like: student@rising.com');
      }
    } catch (error) {
      console.error('Error fetching student data:', error);
      setStudentError('Student not found or error fetching data. Try using email address.');
    } finally {
      setIsLoadingStudent(false);
    }
  };

  // Handle student ID/Email change with debounce
  const handleStudentIdChange = (value) => {
    setStudentInfo(prev => ({ ...prev, studentId: value }));
    
    // Clear previous timeout
    if (window.studentSearchTimeout) {
      clearTimeout(window.studentSearchTimeout);
    }
    
    // Set new timeout for auto-search
    window.studentSearchTimeout = setTimeout(() => {
      if (value.trim().length >= 3) { // Increased to 3 characters for email search
        fetchStudentData(value.trim());
      }
    }, 1000);
  };

  const getExamStructureByClass = (studentClass) => {
    const normalizedClass = studentClass?.toLowerCase() || '';
    
    // Check if it's a preschool class (Nursery, LKG, HKG)
    if (normalizedClass.includes('nursery') || 
        normalizedClass.includes('lkg') || 
        normalizedClass.includes('hkg') ||
        normalizedClass.includes('pre') ||
        normalizedClass.includes('kg')) {
      return [
        { key: 'term1', name: 'Term 1', max: 25 },
        { key: 'term2', name: 'Term 2', max: 25 },
        { key: 'halfYearly', name: 'Half Yearly', max: 100 },
        { key: 'term3', name: 'Term 3', max: 25 },
        { key: 'project', name: 'Project', max: 25 },
        { key: 'annual', name: 'Annual Exam', max: 100 }
      ];
    }
    
    // Default structure for primary/secondary classes
    return [
      { key: 'term1', name: 'Term 1', max: 10 },
      { key: 'term2', name: 'Term 2', max: 10 },
      { key: 'halfYearly', name: 'Half Yearly', max: 80 },
      { key: 'term3', name: 'Term 3', max: 10 },
      { key: 'project', name: 'Project', max: 10 },
      { key: 'annual', name: 'Annual Exam', max: 80 }
    ];
  };

  const getSubjectsByClass = (studentClass) => {
    const normalizedClass = studentClass?.toLowerCase() || '';
    
    // Preschool subjects
    if (normalizedClass.includes('nursery') || 
        normalizedClass.includes('lkg') || 
        normalizedClass.includes('hkg') ||
        normalizedClass.includes('pre') ||
        normalizedClass.includes('kg')) {
      return [
        'English',
        'Hindi', 
        'Mathematics',
        'Drawing',
        'General Knowledge',
        'Rhymes & Stories',
        'Physical Activities',
        'Craft Work'
      ];
    }
    
    // Default subjects for primary/secondary classes
    return [
      'Hindi',
      'English', 
      'Mathematics',
      'Science',
      'Social Studies',
      'Computer Science',
      'Physical Education',
      'Art & Craft'
    ];
  };

  // Get current subjects and exam types based on class
  const getCurrentSubjects = () => getSubjectsByClass(studentInfo.class);
  const getCurrentExamTypes = () => getExamStructureByClass(studentInfo.class);
  const getCurrentMaxMarksPerSubject = () => {
    const examTypes = getCurrentExamTypes();
    return examTypes.reduce((total, exam) => total + exam.max, 0);
  };

  useEffect(() => {
    initializeMarks();
    setCurrentDate();
  }, [studentInfo.class]); // Re-initialize when class changes

  useEffect(() => {
    calculateTotals();
  }, [marks, studentInfo.class]);

  const initializeMarks = () => {
    const initialMarks = {};
    const currentSubjects = getCurrentSubjects();
    const currentExamTypes = getCurrentExamTypes();
    
    currentSubjects.forEach((subject, subjectIndex) => {
      currentExamTypes.forEach(exam => {
        const key = `${subjectIndex}-${exam.key}`;
        initialMarks[key] = '';
      });
    });
    setMarks(initialMarks);
  };

  const setCurrentDate = () => {
    const today = new Date();
    const dateString = today.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    setTimeout(() => {
      const element = document.getElementById('issueDate');
      if (element) {
        element.textContent = dateString;
      }
    }, 100);
  };

  const handleMarkChange = (subjectIndex, examKey, value) => {
    const key = `${subjectIndex}-${examKey}`;
    setMarks(prev => ({
      ...prev,
      [key]: value === '' ? '' : parseInt(value) || 0
    }));
  };

  const calculateTotals = () => {
    const currentSubjects = getCurrentSubjects();
    const currentExamTypes = getCurrentExamTypes();
    const currentMaxMarksPerSubject = getCurrentMaxMarksPerSubject();
    
    const examTotals = {
      term1: 0,
      term2: 0,
      halfYearly: 0,
      term3: 0,
      project: 0,
      annual: 0
    };

    let grandTotal = 0;

    currentSubjects.forEach((subject, subjectIndex) => {
      let subjectTotal = 0;
      currentExamTypes.forEach(exam => {
        const key = `${subjectIndex}-${exam.key}`;
        const value = marks[key] || 0;
        subjectTotal += value;
        examTotals[exam.key] += value;
      });
      grandTotal += subjectTotal;
    });

    const maxMarks = currentSubjects.length * currentMaxMarksPerSubject;
    const percentage = maxMarks > 0 ? ((grandTotal / maxMarks) * 100).toFixed(2) : 0;
    const division = getDivision(percentage);
    const result = percentage >= 33 ? 'PASS' : 'FAIL';
    const overallGrade = getGrade(grandTotal, maxMarks);

    setTotals({
      examTotals,
      grandTotal,
      percentage,
      division,
      result,
      overallGrade,
      maxMarks,
      maxMarksPerSubject: currentMaxMarksPerSubject
    });
  };

  const getGrade = (obtained, total) => {
    if (total === 0) return '-';
    const percentage = (obtained / total) * 100;
    
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B+';
    if (percentage >= 60) return 'B';
    if (percentage >= 50) return 'C+';
    if (percentage >= 40) return 'C';
    if (percentage >= 33) return 'D';
    return 'F';
  };

  const getDivision = (percentage) => {
    if (percentage >= 75) return 'First Division';
    if (percentage >= 60) return 'Second Division';
    if (percentage >= 33) return 'Third Division';
    return 'Fail';
  };

  const getSubjectTotal = (subjectIndex) => {
    let total = 0;
    const currentExamTypes = getCurrentExamTypes();
    currentExamTypes.forEach(exam => {
      const key = `${subjectIndex}-${exam.key}`;
      total += marks[key] || 0;
    });
    return total;
  };

  const getSubjectGrade = (subjectIndex) => {
    const total = getSubjectTotal(subjectIndex);
    const currentMaxMarksPerSubject = getCurrentMaxMarksPerSubject();
    return getGrade(total, currentMaxMarksPerSubject);
  };

  const fillSampleData = () => {
    const currentSubjects = getCurrentSubjects();
    const currentExamTypes = getCurrentExamTypes();
    
    setStudentInfo({
      name: 'Rahul Kumar Sharma',
      fatherName: 'Mr. Suresh Kumar Sharma',
      class: studentInfo.class || 'Nursery',
      studentId: 'RS2024001',
      promotionClass: 'LKG',
      attendance: '92.5%'
    });

    setRemarks({
      drawing: 'A',
      discipline: 'A+',
      conduct: 'Excellent'
    });

    const sampleMarks = currentSubjects.map(() => {
      return currentExamTypes.map(exam => {
        const minMark = Math.floor(exam.max * 0.7);
        const maxMark = Math.floor(exam.max * 0.95);
        return Math.floor(Math.random() * (maxMark - minMark + 1)) + minMark;
      });
    });

    const newMarks = {};
    currentSubjects.forEach((subject, subjectIndex) => {
      currentExamTypes.forEach((exam, examIndex) => {
        const key = `${subjectIndex}-${exam.key}`;
        newMarks[key] = sampleMarks[subjectIndex][examIndex];
      });
    });

    setMarks(newMarks);
  };

  const handlePrint = () => {
    window.print();
  };

  const showPrintHelp = () => {
    alert(`📋 PRINTING INSTRUCTIONS:\n\n1. Click Print or use Ctrl+P\n2. In print dialog:\n   - Select "More settings"\n   - Check "Background graphics"\n   - Set margins to "Minimum"\n   - Choose A4 paper size\n3. For best results, use Chrome or Edge\n4. Enable "Print backgrounds" in browser settings`);
  };

  const isPreschoolClass = () => {
    const normalizedClass = studentInfo.class?.toLowerCase() || '';
    return normalizedClass.includes('nursery') || 
           normalizedClass.includes('lkg') || 
           normalizedClass.includes('hkg') ||
           normalizedClass.includes('pre') ||
           normalizedClass.includes('kg');
  };

  return (
    <div className="professional-marksheet-wrapper">
      {/* Control Panel */}
      <div className="control-panel no-print">
        <h3>📋 Marksheet Controls</h3>
        <div className="control-buttons">
          <button onClick={handlePrint}>🖨️ Print</button>
          <button onClick={showPrintHelp}>❓ Print Help</button>
          <button onClick={fillSampleData}>📝 Sample Data</button>
          {onClose && <button onClick={onClose}>❌ Close</button>}
        </div>
        <hr style={{ margin: '10px 0' }} />
        
        {/* Student Search Section */}
        <div className="student-search-section">
          <h4 style={{ color: '#1e40af', fontSize: '12px', marginBottom: '8px' }}>🔍 Student Information</h4>
          <label>Student Email/ID/Name:</label>
          <input 
            type="text" 
            value={studentInfo.studentId}
            onChange={(e) => handleStudentIdChange(e.target.value)}
            placeholder="Enter Email (Aayara@rising.com) or Student ID"
            style={{ marginBottom: '5px' }}
          />
          <div style={{ fontSize: '9px', color: '#6b7280', marginBottom: '5px' }}>
            💡 Try: Aayara@rising.com, student@rising.com, or student name
          </div>
          {isLoadingStudent && <div style={{ fontSize: '10px', color: '#3b82f6' }}>🔄 Searching...</div>}
          {studentError && <div style={{ fontSize: '10px', color: '#dc2626' }}>❌ {studentError}</div>}
          
          {studentInfo.name && (
            <div style={{ 
              background: '#f0f9ff', 
              padding: '8px', 
              borderRadius: '4px', 
              border: '1px solid #3b82f6',
              marginTop: '5px',
              fontSize: '10px'
            }}>
              <div><strong>Name:</strong> {studentInfo.name}</div>
              <div><strong>Class:</strong> {studentInfo.class}</div>
              <div><strong>Father:</strong> {studentInfo.fatherName}</div>
              {studentInfo.email && <div><strong>Email:</strong> {studentInfo.email}</div>}
              {studentInfo.phone && <div><strong>Phone:</strong> {studentInfo.phone}</div>}
            </div>
          )}
        </div>
        
        <hr style={{ margin: '10px 0' }} />
        <div className="control-inputs">
          <label>School Name:</label>
          <input 
            type="text" 
            value={schoolInfo.name}
            onChange={(e) => setSchoolInfo(prev => ({ ...prev, name: e.target.value }))}
          />
          <label>School Address:</label>
          <input 
            type="text" 
            value={schoolInfo.address}
            onChange={(e) => setSchoolInfo(prev => ({ ...prev, address: e.target.value }))}
          />
          <label>Academic Year:</label>
          <input 
            type="text" 
            value={schoolInfo.academicYear}
            onChange={(e) => setSchoolInfo(prev => ({ ...prev, academicYear: e.target.value }))}
          />
        </div>
      </div>

      <div className="marksheet-container">
        {/* Header */}
        <div className="header">
          <img src="/logo.png" alt="School Logo" className="school-logo" onError={(e) => e.target.style.display = 'none'} />
          <div className="school-name">{schoolInfo.name}</div>
          <div className="school-address">{schoolInfo.address}</div>
          <div className="marksheet-title">FINAL MARKSHEET</div>
        </div>

        {/* Student Information */}
        <div className="student-info">
          <div className="info-group">
            <div className="info-item">
              <span className="info-label">Student Name:</span>
              <input 
                type="text" 
                className="info-value" 
                value={studentInfo.name}
                onChange={(e) => setStudentInfo(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter student name"
              />
            </div>
            <div className="info-item">
              <span className="info-label">Father's Name:</span>
              <input 
                type="text" 
                className="info-value" 
                value={studentInfo.fatherName}
                onChange={(e) => setStudentInfo(prev => ({ ...prev, fatherName: e.target.value }))}
                placeholder="Enter father's name"
              />
            </div>
          </div>
          <div className="info-group">
            <div className="info-item">
              <span className="info-label">Class:</span>
              <input 
                type="text" 
                className="info-value" 
                value={studentInfo.class}
                onChange={(e) => {
                  setStudentInfo(prev => ({ ...prev, class: e.target.value }));
                }}
                placeholder="Enter class (e.g., Nursery, LKG, HKG, 1st, 2nd, etc.)"
              />
            </div>
            <div className="info-item">
              <span className="info-label">Student Email/ID:</span>
              <input 
                type="text" 
                className="info-value" 
                value={studentInfo.studentId}
                onChange={(e) => handleStudentIdChange(e.target.value)}
                placeholder="Enter email (Aayara@rising.com) or student ID"
              />
            </div>
          </div>
          
          {/* Class Type Indicator */}
          <div className="class-type-indicator" style={{ 
            marginTop: '10px', 
            padding: '8px', 
            background: isPreschoolClass() ? '#fef3c7' : '#e0f2fe', 
            borderRadius: '6px',
            border: `1px solid ${isPreschoolClass() ? '#f59e0b' : '#0284c7'}`,
            textAlign: 'center'
          }}>
            <span className="class-type-label" style={{ 
              fontSize: '12px', 
              fontWeight: 'bold',
              color: isPreschoolClass() ? '#92400e' : '#0c4a6e'
            }}>
              📚 Class Type: 
              <strong style={{ marginLeft: '5px' }}>
                {isPreschoolClass() 
                  ? 'Pre-School (Half Yearly & Annual Exams out of 100 marks)' 
                  : 'Primary/Secondary (Half Yearly & Annual Exams out of 80 marks)'}
              </strong>
            </span>
          </div>
        </div>

        {/* Marks Table */}
        <table className="marks-table">
          <thead>
            <tr>
              <th rowSpan="2">SUBJECTS</th>
              {getCurrentExamTypes().map(exam => (
                <th key={exam.key}>{exam.name}</th>
              ))}
              <th rowSpan="2">TOTAL</th>
              <th rowSpan="2">GRADE</th>
            </tr>
            <tr>
              {getCurrentExamTypes().map(exam => (
                <th key={`max-${exam.key}`} style={{ 
                  background: (exam.key === 'halfYearly' || exam.key === 'annual') && exam.max === 100 
                    ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' 
                    : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  color: 'white'
                }}>
                  ({exam.max})
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {getCurrentSubjects().map((subject, subjectIndex) => (
              <tr key={subject}>
                <td className="subject-name">{subject}</td>
                {getCurrentExamTypes().map(exam => (
                  <td key={`${subjectIndex}-${exam.key}`}>
                    <input 
                      type="number" 
                      className="marks-input" 
                      min="0" 
                      max={exam.max}
                      value={marks[`${subjectIndex}-${exam.key}`] || ''}
                      onChange={(e) => handleMarkChange(subjectIndex, exam.key, e.target.value)}
                    />
                  </td>
                ))}
                <td className="total-marks">{getSubjectTotal(subjectIndex)}</td>
                <td className="grade">{getSubjectGrade(subjectIndex)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="total-row">
              <td className="subject-name">GRAND TOTAL</td>
              {getCurrentExamTypes().map(exam => (
                <td key={`total-${exam.key}`}>{totals.examTotals[exam.key]}</td>
              ))}
              <td>{totals.grandTotal}</td>
              <td>{totals.overallGrade}</td>
            </tr>
          </tfoot>
        </table>

        {/* Result Summary */}
        <div className="result-summary">
          <div className="summary-section">
            <div className="summary-title">📊 Academic Performance</div>
            <div className="summary-item">
              <span className="summary-label">Total Marks Obtained:</span>
              <span className="summary-value">{totals.grandTotal}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Maximum Marks:</span>
              <span className="summary-value">{totals.maxMarks} (Per Subject: {totals.maxMarksPerSubject})</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Percentage:</span>
              <span className="summary-value">{totals.percentage}%</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Division:</span>
              <span className="summary-value">{totals.division}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Result:</span>
              <span className="summary-value">{totals.result}</span>
            </div>
          </div>
          <div className="summary-section">
            <div className="summary-title">📋 Additional Information</div>
            <div className="summary-item">
              <span className="summary-label">Promotion to Class:</span>
              <input 
                type="text" 
                className="summary-value-input" 
                value={studentInfo.promotionClass}
                onChange={(e) => setStudentInfo(prev => ({ ...prev, promotionClass: e.target.value }))}
                placeholder="Next class"
              />
            </div>
            <div className="summary-item">
              <span className="summary-label">Attendance:</span>
              <input 
                type="text" 
                className="summary-value-input" 
                value={studentInfo.attendance}
                onChange={(e) => setStudentInfo(prev => ({ ...prev, attendance: e.target.value }))}
                placeholder="85.5%"
              />
            </div>
            <div className="summary-item">
              <span className="summary-label">Academic Year:</span>
              <span className="summary-value">{schoolInfo.academicYear}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Date of Issue:</span>
              <span className="summary-value" id="issueDate"></span>
            </div>
          </div>
        </div>

        {/* Remarks Section */}
        <div className="remarks-section">
          <div className="remark-item">
            <div className="remark-label">Drawing</div>
            <select 
              className="remark-value" 
              value={remarks.drawing}
              onChange={(e) => setRemarks(prev => ({ ...prev, drawing: e.target.value }))}
            >
              <option value="">-</option>
              <option value="A+">A+</option>
              <option value="A">A</option>
              <option value="B+">B+</option>
              <option value="B">B</option>
              <option value="C">C</option>
            </select>
          </div>
          <div className="remark-item">
            <div className="remark-label">Discipline</div>
            <select 
              className="remark-value" 
              value={remarks.discipline}
              onChange={(e) => setRemarks(prev => ({ ...prev, discipline: e.target.value }))}
            >
              <option value="">-</option>
              <option value="A+">A+</option>
              <option value="A">A</option>
              <option value="B+">B+</option>
              <option value="B">B</option>
              <option value="C">C</option>
            </select>
          </div>
          <div className="remark-item">
            <div className="remark-label">Overall Conduct</div>
            <select 
              className="remark-value" 
              value={remarks.conduct}
              onChange={(e) => setRemarks(prev => ({ ...prev, conduct: e.target.value }))}
            >
              <option value="">-</option>
              <option value="Excellent">Excellent</option>
              <option value="Very Good">Very Good</option>
              <option value="Good">Good</option>
              <option value="Satisfactory">Satisfactory</option>
              <option value="Needs Improvement">Needs Improvement</option>
            </select>
          </div>
        </div>

        {/* Signatures */}
        <div className="signatures">
          <div className="signature-item">
            <div className="signature-line"></div>
            <div className="signature-label">Parent/Guardian</div>
          </div>
          <div className="signature-item">
            <div className="signature-line"></div>
            <div className="signature-label">Exam Incharge</div>
          </div>
          <div className="signature-item">
            <div className="signature-line"></div>
            <div className="signature-label">Class Teacher</div>
          </div>
          <div className="signature-item">
            <div className="signature-line"></div>
            <div className="signature-label">Principal</div>
          </div>
          <div className="signature-item">
            <div className="signature-line"></div>
            <div className="signature-label">Director</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalMarksheet;